class TestimonialsSlider extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.total = 0;
    this.isTransitioning = false;
    this.pendingIndex = null;
    this.hasAnimated = false;
    this.tickerRunning = false;
    this.tickerOffset = 0;
    this.tickerLoopWidth = 0;
    this.tickerRaf = null;
    this.autoplayTimer = null;
    this.autoplayResumeTimer = null;
    this.reducedMotion = false;
    this.autoplayEnabled = true;
    this.autoplayInterval = 7000;
    this.tickerSpeed = 30;
    this.pointerInside = false;
    this.focusInside = false;
    this.tabHidden = false;
    this.tickerStarted = false;

    this.onResize = this.onResize.bind(this);
    this.tickerTick = this.tickerTick.bind(this);
    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onPanelNextClick = this.onPanelNextClick.bind(this);
  }

  connectedCallback() {
    this.quoteStories = Array.from(this.querySelectorAll('.ts__story-stack--quote .ts__story'));
    this.metaStories = Array.from(this.querySelectorAll('.ts__story-stack--meta .ts__story'));
    this.panelStacks = Array.from(this.querySelectorAll('[data-panel-stack]'));
    this.panelSlides = this.panelStacks[0]
      ? Array.from(this.panelStacks[0].querySelectorAll('.ts__panel-slide'))
      : [];

    this.nextBtn = this.querySelector('.ts__next-btn');
    this.panelPrevBtns = Array.from(this.querySelectorAll('[data-panel-prev]'));
    this.panelNextBtns = Array.from(this.querySelectorAll('[data-panel-next]'));
    this.panelNextHints = Array.from(this.querySelectorAll('[data-panel-next-hint]'));
    this.panelProgressFills = Array.from(this.querySelectorAll('[data-panel-progress-fill]'));
    this.panelProgressCurrents = Array.from(this.querySelectorAll('[data-panel-progress-current]'));
    this.panelProgressTotals = Array.from(this.querySelectorAll('[data-panel-progress-total]'));
    this.panelNextMetas = Array.from(this.querySelectorAll('[data-panel-next-meta]'));

    this.counterCurrent = this.querySelector('[data-counter-current]');
    this.counterTotal = this.querySelector('[data-counter-total]');
    this.tickerStage = this.querySelector('[data-ticker-stage]');
    this.tickerTrack = this.querySelector('[data-ticker-track]');
    this.featuredArea = this.querySelector('[data-featured-area]');

    this.blockData = this.panelSlides.map((slide) => ({
      name: slide.dataset.name || 'Customer',
      num: slide.dataset.num || '01',
    }));

    this.total = this.quoteStories.length;
    if (!this.total) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.autoplayEnabled = this.dataset.autoplay !== 'false';
    this.autoplayInterval = parseInt(this.dataset.autoplayInterval, 10) || 7000;
    this.updateTickerSpeed();

    const defaultActive = parseInt(this.dataset.defaultActive, 10) || 0;
    this.activeIndex = Math.min(Math.max(0, defaultActive), this.total - 1);

    this.deckStack = this.querySelector('.ts__story-stack--quote');
    if (!this.reducedMotion && this.deckStack) this.classList.add('ts--deck');

    this.bindEvents();
    this.setActive(this.activeIndex, { immediate: true });
    this.initObserver();

    this.pointerQuery.addEventListener('change', this.onResize);
    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  disconnectedCallback() {
    this.stopTicker();
    this.stopAutoplay();
    this.observer?.disconnect();
    this.pointerQuery?.removeEventListener('change', this.onResize);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.removeInteractionListeners();
  }

  updateTickerSpeed() {
    const desktop = parseFloat(this.dataset.tickerSpeedDesktop) || 30;
    const mobile = parseFloat(this.dataset.tickerSpeedMobile) || 22;
    this.tickerSpeed = window.matchMedia('(max-width: 989px)').matches ? mobile : desktop;
  }

  bindEvents() {
    this.nextBtn?.addEventListener('click', this.onNextClick);
    this.panelPrevBtns.forEach((btn) => btn.addEventListener('click', this.onPrevClick));
    this.panelNextBtns.forEach((btn) => btn.addEventListener('click', this.onPanelNextClick));
    this.panelNextHints.forEach((btn) => btn.addEventListener('click', this.onPanelNextClick));

    this.addInteractionListeners();

    this.touchStartX = 0;
    this.touchStartY = 0;

    if (this.featuredArea) {
      this.featuredArea.addEventListener('touchstart', (event) => {
        if (!event.touches?.length) return;
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
      }, { passive: true });

      // The deck leans with the finger while it moves horizontally.
      this.featuredArea.addEventListener('touchmove', (event) => {
        if (!this.deckStack || this.reducedMotion || !event.touches?.length) return;
        const dx = event.touches[0].clientX - this.touchStartX;
        const dy = event.touches[0].clientY - this.touchStartY;
        if (Math.abs(dx) < 8 || Math.abs(dy) > Math.abs(dx)) return;
        const width = this.deckStack.offsetWidth || 1;
        this.deckStack.classList.add('is-dragging');
        this.deckStack.style.setProperty('--ts-drag', Math.max(-1, Math.min(1, dx / width)).toFixed(3));
      }, { passive: true });

      const releaseDrag = () => {
        if (!this.deckStack) return;
        this.deckStack.classList.remove('is-dragging');
        this.deckStack.style.setProperty('--ts-drag', '0');
      };
      this.featuredArea.addEventListener('touchcancel', releaseDrag, { passive: true });

      this.featuredArea.addEventListener('touchend', (event) => {
        releaseDrag();
        if (!event.changedTouches?.length || this.reducedMotion) return;
        const deltaX = event.changedTouches[0].clientX - this.touchStartX;
        const deltaY = event.changedTouches[0].clientY - this.touchStartY;
        if (Math.abs(deltaX) < 40 || Math.abs(deltaY) > Math.abs(deltaX)) return;
        this.pauseAutoplay();
        if (deltaX < 0) {
          this.goTo((this.activeIndex + 1) % this.total, { userInitiated: true });
        } else {
          this.goTo((this.activeIndex - 1 + this.total) % this.total, { userInitiated: true });
        }
        this.scheduleAutoplayResume();
      }, { passive: true });
    }
  }

  addInteractionListeners() {
    this.addEventListener('pointerenter', this.onPointerEnter);
    this.addEventListener('pointerleave', this.onPointerLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
  }

  removeInteractionListeners() {
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
  }

  initObserver() {
    if (this.reducedMotion) {
      this.classList.add('is-visible', 'is-band-visible', 'is-index-visible');
      this.startTicker();
      if (this.autoplayEnabled) this.startAutoplay();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.hasAnimated = true;
            this.runEntrance();
            this.observer.disconnect();
          }
        });
      },
      { threshold: 0.18, rootMargin: '0px 0px -5% 0px' }
    );

    this.observer.observe(this);
  }

  runEntrance() {
    this.classList.add('is-visible');
    window.setTimeout(() => this.classList.add('is-band-visible'), 480);
    window.setTimeout(() => this.classList.add('is-index-visible'), 620);
    window.setTimeout(() => {
      this.startTicker();
      if (this.autoplayEnabled) this.startAutoplay();
    }, 920);
  }

  measureTicker() {
    if (!this.tickerTrack) return;
    this.tickerLoopWidth = this.tickerTrack.scrollWidth;
  }

  startTicker() {
    if (this.reducedMotion || this.tickerStarted || !this.tickerStage) return;

    this.tickerStarted = true;
    this.measureTicker();
    if (this.tickerLoopWidth <= 0) return;

    this.tickerRunning = true;
    this.lastTickerTime = performance.now();
    this.tickerRaf = requestAnimationFrame(this.tickerTick);
  }

  stopTicker() {
    this.tickerRunning = false;
    if (this.tickerRaf) {
      cancelAnimationFrame(this.tickerRaf);
      this.tickerRaf = null;
    }
  }

  tickerTick(now) {
    if (!this.tickerRunning) return;

    const delta = Math.min(now - this.lastTickerTime, 48);
    this.lastTickerTime = now;

    if (this.tickerLoopWidth > 0) {
      this.tickerOffset -= (this.tickerSpeed * delta) / 1000;
      while (this.tickerOffset <= -this.tickerLoopWidth) {
        this.tickerOffset += this.tickerLoopWidth;
      }
      this.tickerStage.style.transform = `translate3d(${this.tickerOffset}px, 0, 0)`;
    }

    this.tickerRaf = requestAnimationFrame(this.tickerTick);
  }

  onResize() {
    this.updateTickerSpeed();
    this.measureTicker();
  }

  onPointerEnter() {
    this.pointerInside = true;
    this.pauseAutoplay();
  }

  onPointerLeave() {
    this.pointerInside = false;
    this.scheduleAutoplayResume();
  }

  onFocusIn() {
    this.focusInside = true;
    this.pauseAutoplay();
  }

  onFocusOut(event) {
    if (this.contains(event.relatedTarget)) return;
    this.focusInside = false;
    this.scheduleAutoplayResume();
  }

  onVisibilityChange() {
    this.tabHidden = document.hidden;
    if (this.tabHidden) {
      this.pauseAutoplay();
    } else {
      this.scheduleAutoplayResume();
    }
  }

  shouldAutoplay() {
    return (
      this.autoplayEnabled &&
      !this.reducedMotion &&
      !this.pointerInside &&
      !this.focusInside &&
      !this.tabHidden &&
      !this.isTransitioning &&
      this.classList.contains('is-visible')
    );
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.shouldAutoplay()) return;

    this.autoplayTimer = window.setInterval(() => {
      if (this.shouldAutoplay()) {
        this.goTo((this.activeIndex + 1) % this.total, { fromAutoplay: true });
      }
    }, this.autoplayInterval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  pauseAutoplay() {
    this.stopAutoplay();
    if (this.autoplayResumeTimer) {
      clearTimeout(this.autoplayResumeTimer);
      this.autoplayResumeTimer = null;
    }
  }

  scheduleAutoplayResume(delay = 4000) {
    if (this.autoplayResumeTimer) clearTimeout(this.autoplayResumeTimer);
    this.autoplayResumeTimer = window.setTimeout(() => {
      this.startAutoplay();
    }, delay);
  }

  onNextClick(event) {
    event.preventDefault();
    this.pauseAutoplay();
    this.goTo((this.activeIndex + 1) % this.total, { userInitiated: true });
    this.scheduleAutoplayResume();
  }

  onPrevClick(event) {
    event.preventDefault();
    this.pauseAutoplay();
    this.goTo((this.activeIndex - 1 + this.total) % this.total, { userInitiated: true });
    this.scheduleAutoplayResume();
  }

  onPanelNextClick(event) {
    event.preventDefault();
    this.pauseAutoplay();
    this.goTo((this.activeIndex + 1) % this.total, { userInitiated: true });
    this.scheduleAutoplayResume();
  }

  getSlidesFromStack(stack, index) {
    const slides = Array.from(stack.querySelectorAll('.ts__panel-slide'));
    return slides[index] || null;
  }

  animatePanelStacks(fromIndex, toIndex) {
    this.classList.add('is-panel-transitioning');

    this.panelStacks.forEach((stack) => {
      const slides = Array.from(stack.querySelectorAll('.ts__panel-slide'));
      const outgoing = slides[fromIndex];
      const incoming = slides[toIndex];

      outgoing?.classList.add('is-leaving');
      outgoing?.classList.remove('is-active');

      window.setTimeout(() => {
        incoming?.classList.add('is-entering', 'is-active');

        window.setTimeout(() => {
          slides.forEach((slide, i) => {
            slide.classList.toggle('is-active', i === toIndex);
            slide.classList.remove('is-leaving', 'is-entering');
          });
        }, 650);
      }, 140);
    });

    window.setTimeout(() => {
      this.classList.remove('is-panel-transitioning');
    }, 680);
  }

  setPanelActive(index, { animate = false, fromIndex = null } = {}) {
    if (animate && fromIndex !== null && fromIndex !== index && !this.reducedMotion) {
      this.animatePanelStacks(fromIndex, index);
      this.updatePanelMeta(index, { deferHint: true });
      window.setTimeout(() => this.updateNextHint(index), 650);
    } else {
      this.panelStacks.forEach((stack) => {
        const slides = Array.from(stack.querySelectorAll('.ts__panel-slide'));
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === index);
          slide.classList.remove('is-leaving', 'is-entering');
        });
      });
      this.updatePanelMeta(index);
    }
  }

  updateNextHint(index) {
    const nextIndex = (index + 1) % this.total;
    const nextBlock = this.blockData[nextIndex];
    this.panelNextMetas.forEach((el) => {
      el.textContent = `${nextBlock.num} — ${nextBlock.name}`;
    });
  }

  updatePanelMeta(index, { deferHint = false } = {}) {
    const num = String(index + 1).padStart(2, '0');
    const total = String(this.total).padStart(2, '0');
    const progress = this.total > 0 ? ((index + 1) / this.total) * 100 : 0;

    this.panelProgressCurrents.forEach((el) => {
      el.textContent = num;
    });
    this.panelProgressTotals.forEach((el) => {
      el.textContent = total;
    });
    this.panelProgressFills.forEach((el) => {
      el.style.setProperty('--ts-progress', `${progress}%`);
    });

    if (!deferHint) {
      this.updateNextHint(index);
    }

    this.updateCounter(index);
  }

  goTo(index, { immediate = false, userInitiated = false } = {}) {
    if (index < 0 || index >= this.total || index === this.activeIndex) return;

    if (immediate || this.reducedMotion) {
      this.setActive(index, { immediate: true });
      return;
    }

    if (this.isTransitioning) {
      this.pendingIndex = index;
      return;
    }

    const fromIndex = this.activeIndex;
    this.isTransitioning = true;
    this.updateDeck(index);
    this.classList.add('is-transitioning', 'is-gold-sweep', 'is-panel-transitioning');

    const outgoingQuote = this.quoteStories[fromIndex];
    const incomingQuote = this.quoteStories[index];
    const outgoingMeta = this.metaStories[fromIndex];
    const incomingMeta = this.metaStories[index];

    outgoingQuote?.classList.add('is-leaving');
    outgoingQuote?.classList.remove('is-active');
    outgoingMeta?.classList.add('is-leaving');
    outgoingMeta?.classList.remove('is-active');

    this.setPanelActive(index, { animate: true, fromIndex });

    window.setTimeout(() => {
      incomingQuote?.classList.add('is-entering', 'is-active');
      incomingMeta?.classList.add('is-entering', 'is-active');

      window.setTimeout(() => {
        [outgoingQuote, outgoingMeta, incomingQuote, incomingMeta].forEach((el) => {
          el?.classList.remove('is-leaving', 'is-entering');
        });

        this.quoteStories.forEach((story, i) => story.classList.toggle('is-active', i === index));
        this.metaStories.forEach((story, i) => story.classList.toggle('is-active', i === index));

        this.activeIndex = index;
        this.isTransitioning = false;
        this.classList.remove('is-transitioning', 'is-gold-sweep', 'is-panel-transitioning');

        if (this.pendingIndex !== null && this.pendingIndex !== this.activeIndex) {
          const next = this.pendingIndex;
          this.pendingIndex = null;
          this.goTo(next, { userInitiated });
        } else {
          this.pendingIndex = null;
        }
      }, 720);
    }, 180);
  }

  // Positions every quote card in the 3D deck relative to the active one:
  // --o is the signed distance (wrapped to the shortest way round).
  updateDeck(index) {
    if (!this.classList.contains('ts--deck')) return;
    const n = this.total;
    this.quoteStories.forEach((story, i) => {
      let o = i - index;
      if (o > n / 2) o -= n;
      if (o < -n / 2) o += n;
      story.style.setProperty('--o', o);
      story.style.setProperty('--ao', Math.abs(o));
      story.style.zIndex = String(10 - Math.abs(o));
      story.toggleAttribute('data-deck-far', Math.abs(o) > 2);
    });
  }

  setActive(index, { immediate = false } = {}) {
    this.activeIndex = index;
    this.updateDeck(index);

    this.quoteStories.forEach((story, i) => {
      story.classList.toggle('is-active', i === index);
      story.classList.remove('is-leaving', 'is-entering');
    });
    this.metaStories.forEach((story, i) => {
      story.classList.toggle('is-active', i === index);
      story.classList.remove('is-leaving', 'is-entering');
    });

    this.setPanelActive(index, { animate: false });
    this.updatePanelMeta(index);

    if (immediate) {
      this.classList.remove('is-transitioning', 'is-gold-sweep', 'is-panel-transitioning');
    }
  }

  updateCounter(index) {
    const num = String(index + 1).padStart(2, '0');
    const total = String(this.total).padStart(2, '0');
    if (this.counterCurrent) this.counterCurrent.textContent = num;
    if (this.counterTotal) this.counterTotal.textContent = total;
  }
}

if (!customElements.get('testimonials-slider')) {
  customElements.define('testimonials-slider', TestimonialsSlider);
}
